// src/services/aiInsightsService.js
// ══════════════════════════════════════════════════════════════════════════════════════
// Enterprise AI Insights Service (Work Orders)
// ══════════════════════════════════════════════════════════════════════════════════════
// Provides intelligent analysis, connects to the FlowSense ML Backend, and manages
// deep local heuristic fallbacks.
//
// UPDATE: Implemented dedicated Axios Instance with Request/Response Interceptors
// for automated JWT Bearer Token injection to resolve 401 Unauthorized errors.
//
// --- UPDATE (AI Insights v3) fully connected to the FlowSense AI/ML system:
//   * server insights from /api/v1/ai-insights/work-orders (live SQL + ML engine forecasts/anomalies
//     + Ollama briefing + feedback learning), legacy /analytics/ai-insights kept as a second source
//   * merged with the local heuristics (dedupe by insight key) so the panel never goes empty
//   * single-flight requests, short response cache, cancellation, live refresh over WebSocket
//   * ratings go to the feedback loop (re-weights / mutes insight types for everyone)
//   * local heuristics fixed: derive stats when missing, real durations from started_at/completed_at,
//     SLA deadlines, no invented percentages in messages
// --- END UPDATE
// ══════════════════════════════════════════════════════════════════════════════════════

import axios from 'axios';
import { config } from '../config';
import { getAuthToken } from '../utils/auth'; // --- UPDATE: same token source as the rest of FlowSense
import webSocketService from './websocketService'; // --- UPDATE: live refresh

// ============================================================================
// AXIOS CLIENT SETUP & INTERCEPTORS
// ============================================================================

const apiClient = axios.create({
  // Use config.apiUrl which points to https://flowsense.live/api/v1
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 15000,
});

// --- UPDATE: one token lookup used by every request
const readToken = () => {
  try {
    return (typeof getAuthToken === 'function' && getAuthToken())
      || localStorage.getItem('jwt_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
  } catch (_) {
    return null;
  }
};
// --- END UPDATE

/**
 * REQUEST INTERCEPTOR:
 * Automatically injects the authentication token into every outgoing request.
 * This resolves the 401 Unauthorized errors when communicating with the ML engine.
 */
apiClient.interceptors.request.use(
  (reqConfig) => {
    const token = readToken();

    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }

    // Attach a unique request ID for tracing in backend logs
    reqConfig.headers['X-Request-ID'] = `req-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    return reqConfig;
  },
  (error) => {
    console.error('[AIInsightsService] Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR:
 * Globally handles 401 token expirations and other HTTP errors.
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    // Handle 401 Unauthorized (Token Expired or Invalid)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.warn('[AIInsightsService] 401 Unauthorized detected. Token may be expired.');
      // For now, dispatch an event so the UI can redirect to login
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    // Handle 503 Service Unavailable (ML Engine Offline)
    if (error.response && error.response.status === 503) {
      console.warn('[AIInsightsService] ML Engine is currently offline or unreachable (503). Falling back to local heuristics.');
    }

    return Promise.reject(error);
  }
);

// --- UPDATE: local insight ids -> server insight keys, so ratings and de-duplication line up
const LOCAL_TO_SERVER = {
  'local-overdue': 'wo.overdue',
  'local-unassigned': 'wo.unassigned',
  'local-priority': 'wo.priority',
  'local-completion-high': 'wo.completion_high',
  'local-completion-low': 'wo.completion_low',
  'local-workload': 'wo.workload',
  'local-trend-backlog': 'wo.backlog_forecast',
  'local-resources': 'wo.resources',
  'local-sla-risk': 'wo.sla_risk',
  'local-anomaly-duration': 'wo.duration_anomaly',
  'local-asset-clusters': 'wo.repeat_assets',
  'local-on-hold': 'wo.on_hold',
  'local-public-backlog': 'wo.public_backlog',
};
const ACTIVE = (s) => !['completed', 'cancelled', 'canceled', 'closed', 'rejected'].includes(s);
const NOT_STARTED = ['pending', 'open', 'assigned', 'auto_assigned', 'scheduled', 'unassigned'];
const deadlineOf = (wo) => wo.sla_resolution_deadline || wo.due_date || null;
const hoursBetween = (a, b) => (new Date(b).getTime() - new Date(a).getTime()) / 3600000;
// --- END UPDATE


// ============================================================================
// AI INSIGHTS CLASS DEFINITION
// ============================================================================

class AIInsightsService {
  constructor() {
    this.debugMode = process.env.NODE_ENV !== 'production';
    this.cache = new Map();
    this.cacheTTL = 300000; // 5 minutes standard cache life
    this.maxRetries = 2;    // Max retries for ML API failures
    // --- UPDATE
    this.serverTTL = 30000;      // server insights are already cached 60 s server-side
    this.inflight = new Map();
    this.lastMeta = null;
    this.version = '3.0.0';
    // --- END UPDATE
  }

  // ────────────────────────────────────────────────────────────────────────────
  // CORE UTILITIES & LOGGING
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Enable or disable debug logging
   * @param {boolean} enabled
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    this.log('info', 'Debug mode state updated', { enabled });
  }

  /**
   * Centralized debug logger with formatted outputs
   * @param {string} level - 'info', 'warn', 'error', 'debug'
   * @param {string} message - Descriptive message
   * @param {Object} data - Optional payload
   */
  log(level, message, data = null) {
    if (!this.debugMode && level !== 'error') return;

    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      service: 'AIInsightsService',
      message,
      data,
    };

    const consoleMethod = console[level] || console.log;

    // Use styling for browser console if available
    if (typeof window !== 'undefined') {
      const colors = {
        info: 'color: #007bff; font-weight: bold;',
        warn: 'color: #ffc107; font-weight: bold;',
        error: 'color: #dc3545; font-weight: bold;',
        debug: 'color: #6c757d; font-style: italic;',
      };
      consoleMethod(`%c[${timestamp}] [AIInsights] [${level.toUpperCase()}] ${message}`, colors[level] || '', data || '');
    } else {
      consoleMethod(`[${timestamp}] [AIInsights] [${level.toUpperCase()}] ${message}`, data ? JSON.stringify(data) : '');
    }

    return logEntry;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // CACHE MANAGEMENT
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Retrieve valid data from the memory cache
   * @param {string} key
   * @param {number} ttl optional override
   * @returns {any|null}
   */
  getCached(key, ttl = this.cacheTTL) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > ttl) {
      this.cache.delete(key);
      this.log('debug', 'Cache expired and evicted', { key });
      return null;
    }

    // refresh LRU position
    this.cache.delete(key);
    this.cache.set(key, cached);
    this.log('debug', 'Cache hit successfully', { key });
    return cached.data;
  }

  /**
   * Set data into the memory cache
   * @param {string} key
   * @param {any} data
   */
  setCached(key, data) {
    // Implement simple LRU logic if cache gets too large (e.g., > 100 entries)
    if (this.cache.size >= 100) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
    this.log('debug', 'Payload cached', { key, size: this.cache.size });
  }

  /**
   * Clear all cache
   */
  clearCache() {
    const size = this.cache.size;
    this.cache.clear();
    this.log('info', 'Cache fully cleared', { previousSize: size });
  }

  /**
   * Get cache diagnostics
   */
  getCacheStats() {
    const stats = {
      size: this.cache.size,
      ttl: this.cacheTTL,
      keys: Array.from(this.cache.keys()),
      memoryEstimate: `${(JSON.stringify(Array.from(this.cache.entries())).length / 1024).toFixed(2)} KB`,
    };

    this.log('debug', 'Cache statistics generated', stats);
    return stats;
  }

  // --- UPDATE: request helpers (single-flight + retry only on network / 5xx)
  async _request(method, url, { params, data, signal } = {}) {
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const res = await apiClient.request({ method, url, params, data, signal });
        return res.data;
      } catch (error) {
        const status = error.response?.status;
        const retryable = !status || status >= 500;
        if (axios.isCancel?.(error) || error.name === 'CanceledError' || !retryable || attempt >= this.maxRetries) throw error;
        const delay = 500 * 2 ** attempt;
        attempt += 1;
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  _once(key, fn) {
    if (this.inflight.has(key)) return this.inflight.get(key);
    const p = fn().finally(() => this.inflight.delete(key));
    this.inflight.set(key, p);
    return p;
  }
  // --- END UPDATE

  // ────────────────────────────────────────────────────────────────────────────
  // BACKEND API COMMUNICATION (Using Authenticated Interceptor Client)
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Fetches AI-generated insights from the backend (ML models).
   * --- UPDATE: tries the FlowSense AI Insights engine first, then the legacy analytics route.
   * @param {Object} opts { days, narrative, refresh, signal }
   * @returns {Promise<Object>} { success, data, meta, stats, narrative, source }
   */
  async getInsights(opts = {}) {
    const { days = 30, narrative = true, refresh = false, signal } = opts;
    const key = `server-wo-${days}-${narrative}`;
    if (!refresh) {
      const hit = this.getCached(key, this.serverTTL);
      if (hit) return hit;
    }
    return this._once(key, async () => {
      try {
        const body = await this._request('get', '/ai-insights/work-orders', { params: { days, narrative: narrative ? 1 : 0, refresh: refresh ? 1 : 0 }, signal });
        const out = { success: true, data: body.data || [], meta: body.meta || null, stats: body.stats || null, narrative: body.narrative || null, source: 'ai-insights' };
        this.lastMeta = { ...out.meta, narrative: out.narrative, stats: out.stats, source: out.source };
        this.setCached(key, out);
        this.log('info', 'Fetched server-side AI insights', { count: out.data.length, ms: out.meta?.durationMs, cached: out.meta?.cached });
        return out;
      } catch (error) {
        if (error.response?.status === 401) {
          this.log('error', 'Authentication failed (401). Aborting.');
          return { success: false, data: [], error: 'unauthorized' };
        }
        this.log('warn', 'AI insights engine unavailable, trying legacy analytics route', error.message);
      }
      try {
        const body = await this._request('get', '/analytics/ai-insights', { signal });
        const out = { success: body?.success !== false, data: Array.isArray(body?.data) ? body.data : [], meta: null, stats: null, narrative: null, source: 'analytics' };
        this.setCached(key, out);
        return out;
      } catch (error) {
        this.log('error', 'Exhausted server sources for AI insights', error.message);
        // Return a safe fallback structure so the UI doesn't crash
        return { success: false, data: [] };
      }
    });
  }

  /**
   * --- UPDATE: server + local insights in one list (what the dashboard panel should call).
   * Server insights win; local heuristics fill in anything the server did not report.
   * @returns {Promise<Array>}
   */
  async getMergedInsights(workOrders = [], stats = {}, opts = {}) {
    const local = this.generateInsights(workOrders, stats);
    const server = await this.getInsights(opts);
    if (!server.success || !server.data.length) return local;
    const serverKeys = new Set(server.data.map((i) => i.key || i.id));
    const extra = local.filter((i) => !serverKeys.has(LOCAL_TO_SERVER[i.id] || i.id));
    const list = [...server.data, ...extra];
    if (server.narrative) {
      list.unshift({
        id: 'workorders:wo.briefing', key: 'wo.briefing', type: 'info', severity: 'info', icon: 'MdAutoAwesome',
        title: 'AI operations briefing', message: server.narrative, action: null, source: 'server', engine: 'llm',
        metrics: {}, recommendations: [],
      });
    }
    return list;
  }

  /** --- UPDATE: cross-module top insights + AI briefings (dashboard home) */
  async getSummary({ limit = 8, narrative = true, signal } = {}) {
    try {
      const body = await this._request('get', '/ai-insights/summary', { params: { limit, narrative: narrative ? 1 : 0 }, signal });
      return body.data;
    } catch (error) {
      this.log('warn', 'Insight summary unavailable', error.message);
      return null;
    }
  }

  /** --- UPDATE: engine health (ML engine, LLM, feedback table) */
  async getStatus() {
    try { return (await this._request('get', '/ai-insights/status')).data; } catch (_) { return null; }
  }

  /** --- UPDATE: daily insight counts for trend charts */
  async getHistory(days = 30) {
    try { return (await this._request('get', '/ai-insights/history/workorders', { params: { days } })).data || []; } catch (_) { return []; }
  }

  /** --- UPDATE: ratings per insight type (admins) */
  async getFeedbackStats(days = 90) {
    try { return (await this._request('get', '/ai-insights/feedback', { params: { days } })).data || []; } catch (_) { return []; }
  }

  /** --- UPDATE: last server response context (briefing, engine timing, stats) for the UI */
  getLastMeta() {
    return this.lastMeta;
  }

  /**
   * Sends feedback on an insight (helpful/not helpful) to retrain the model.
   * @param {string|Object} insightOrId - insight object or id
   * @param {number} rating - 1 for helpful, -1 for not helpful
   * @param {string} comment - optional
   */
  async rateInsight(insightOrId, rating, comment = null) {
    const insight = typeof insightOrId === 'object' && insightOrId ? insightOrId : null;
    const rawId = insight ? insight.id : insightOrId;
    // --- UPDATE: map local ids to the server key so feedback trains the shared engine
    const serverId = String(rawId || '').includes(':') ? rawId : `workorders:${LOCAL_TO_SERVER[rawId] || rawId}`;
    try {
      const body = await this._request('post', `/ai-insights/${encodeURIComponent(serverId)}/rate`, {
        data: { rating, comment, insight: insight ? { title: insight.title, metrics: insight.metrics, severity: insight.severity } : null },
      });
      this.clearCache();
      this.log('info', 'Insight rated, feedback sent to the AI insights engine', { serverId, rating });
      return body;
    } catch (error) {
      this.log('warn', 'AI insights feedback failed, trying legacy analytics route', error.message);
      try {
        const response = await apiClient.post(`/analytics/ai-insights/${encodeURIComponent(rawId)}/rate`, { rating });
        return response.data;
      } catch (legacyError) {
        this.log('error', `Failed to submit rating for insight ${rawId}`, legacyError);
        throw legacyError;
      }
    }
    // --- END UPDATE
  }

  /**
   * --- UPDATE: live refresh. Calls onChange (debounced) when work orders change anywhere.
   * @returns {Function} unsubscribe
   */
  watch(onChange, { debounceMs = 3000 } = {}) {
    let t = null;
    const fire = () => {
      clearTimeout(t);
      t = setTimeout(() => { this.clearCache(); onChange && onChange(); }, debounceMs);
    };
    const unsubs = [];
    try {
      webSocketService.connect();
      const u1 = webSocketService.subscribe('work_order_events', fire);
      const u2 = webSocketService.subscribe('work_orders', fire);
      unsubs.push(
        typeof u1 === 'function' ? u1 : () => webSocketService.unsubscribe?.('work_order_events', fire),
        typeof u2 === 'function' ? u2 : () => webSocketService.unsubscribe?.('work_orders', fire),
      );
    } catch (e) {
      this.log('warn', 'Live insight refresh unavailable', e.message);
    }
    return () => { clearTimeout(t); unsubs.forEach((u) => { try { u(); } catch (_) { /* ignore */ } }); };
  }
  // --- END UPDATE

  // ────────────────────────────────────────────────────────────────────────────
  // LOCAL HEURISTIC ANALYSIS (Edge-Compute Fallbacks)
  // ────────────────────────────────────────────────────────────────────────────

  /** --- UPDATE: build the stats the analyzers need when the caller passes none / partial stats */
  _deriveStats(workOrders = [], stats = {}) {
    const now = Date.now();
    const active = workOrders.filter((w) => ACTIVE(w.status));
    const derived = {
      total: workOrders.length,
      pending: workOrders.filter((w) => w.status === 'pending').length,
      in_progress: workOrders.filter((w) => w.status === 'in_progress').length,
      completed: workOrders.filter((w) => w.status === 'completed').length,
      unassigned: active.filter((w) => !w.assigned_user_id && !w.assigned_team_id && !w.assigned_to).length,
      overdue: active.filter((w) => deadlineOf(w) && new Date(deadlineOf(w)).getTime() < now).length,
      high_priority: active.filter((w) => ['critical', 'high', 'urgent'].includes(w.priority)).length,
    };
    const out = { ...derived };
    Object.keys(stats || {}).forEach((k) => { if (stats[k] !== undefined && stats[k] !== null) out[k] = Number(stats[k]); });
    return out;
  }

  _fingerprint(workOrders) {
    let h = 0;
    for (const w of workOrders) {
      const s = `${w.id}|${w.status}|${w.updated_at || ''}|${w.assigned_user_id || ''}`;
      for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return `${workOrders.length}:${h}`;
  }
  // --- END UPDATE

  /**
   * Analyze work orders and generate insights locally.
   * This is used as a fallback or immediate computation mechanism.
   * @param {Array} workOrders - Array of work order objects
   * @param {Object} stats - Work order statistics
   * @returns {Array} - Array of insight objects
   */
  generateInsights(workOrders = [], stats = {}) {
    const startTime = performance.now();
    if (!Array.isArray(workOrders)) workOrders = [];
    const s = this._deriveStats(workOrders, stats); // --- UPDATE
    this.log('info', 'Executing local heuristic insight generation', {
      workOrderCount: workOrders.length,
    });

    try {
      const insights = [];

      // Generate a deterministic cache key (--- UPDATE: content fingerprint, not just counts)
      const cacheKey = `local-insights-${this._fingerprint(workOrders)}-${JSON.stringify(s)}`;
      const cached = this.getCached(cacheKey);
      if (cached) {
        this.log('info', 'Returning cached local insights', { count: cached.length });
        return cached;
      }

      // Execute all heuristic analyzers safely
      const analyzers = [
        this.analyzeOverdue.bind(this),
        this.analyzeUnassigned.bind(this),
        this.analyzePriority.bind(this),
        this.analyzeCompletionRate.bind(this),
        this.analyzeWorkload.bind(this),
        this.analyzeTrends.bind(this),
        this.analyzeResources.bind(this),
        this.analyzeSLAAdherence.bind(this),
        this.detectAnomalousDurations.bind(this),
        this.analyzeAssetFailurePatterns.bind(this),
        this.analyzeOnHold.bind(this),          // --- UPDATE
        this.analyzePublicReports.bind(this),   // --- UPDATE
      ];

      analyzers.forEach((analyzer) => {
        try {
          const result = analyzer(workOrders, s);
          if (result) {
            // Handle both single objects and arrays returned from analyzers
            if (Array.isArray(result)) {
              insights.push(...result);
            } else {
              insights.push(result);
            }
          }
        } catch (analyzerError) {
          this.log('error', 'Analyzer failed execution', analyzerError);
        }
      });

      // --- UPDATE: tag + rank (critical first)
      const rank = { critical: 0, warning: 1, info: 2, success: 3 };
      insights.forEach((i) => { i.source = 'local'; i.key = LOCAL_TO_SERVER[i.id] || i.id; });
      insights.sort((a, b) => (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9));
      // --- END UPDATE

      // Set in cache
      this.setCached(cacheKey, insights);

      const duration = performance.now() - startTime;
      this.log('info', 'Local insights generated successfully', {
        count: insights.length,
        duration: `${duration.toFixed(2)}ms`,
      });

      return insights;
    } catch (error) {
      this.log('error', 'Catastrophic error generating local insights', {
        error: error.message,
        stack: error.stack,
      });
      return [];
    }
  }

  /**
   * 1. Analyze overdue work orders
   */
  analyzeOverdue(workOrders, stats) {
    const overdueCount = stats.overdue || 0;
    const threshold = 5;

    if (overdueCount > threshold) {
      const severity = overdueCount > 20 ? 'critical' : overdueCount > 10 ? 'warning' : 'info';

      return {
        id: 'local-overdue',
        type: 'warning',
        severity,
        icon: 'MdWarning',
        title: 'High Overdue Backlog',
        message: `${overdueCount} work orders are currently overdue. This risks SLA violations. Consider immediate reassignment or priority escalation.`,
        action: 'View Overdue',
        actionType: 'filter',
        actionPayload: { filter: 'overdue' },
        metrics: {
          count: overdueCount,
          percentage: stats.total > 0 ? ((overdueCount / stats.total) * 100).toFixed(1) : 0,
        },
        recommendations: [
          'Review and reassign overdue tasks to available technicians.',
          'Consider temporarily increasing team capacity (overtime) for high-priority items.',
          'Implement automated escalation for tasks overdue by more than 48 hours.',
        ],
      };
    }
    return null;
  }

  /**
   * 2. Analyze unassigned work orders
   */
  analyzeUnassigned(workOrders, stats) {
    const unassignedCount = stats.unassigned || 0;
    const threshold = 10;

    if (unassignedCount > threshold) {
      return {
        id: 'local-unassigned',
        type: 'info',
        severity: 'warning',
        icon: 'MdPersonAdd',
        title: 'High Volume Unassigned Work',
        message: `${unassignedCount} work orders require immediate assignment. Unassigned tickets artificially inflate mean-time-to-resolution (MTTR).`,
        action: 'Auto-Assign',
        actionType: 'bulk-assign',
        actionPayload: { assignUnassigned: true },
        metrics: {
          count: unassignedCount,
          percentage: stats.total > 0 ? ((unassignedCount / stats.total) * 100).toFixed(1) : 0,
        },
        recommendations: [
          'Enable ML-driven auto-assignment based on technician geographic proximity and workload.',
          'Review technician availability calendars and update statuses.',
          'Prioritize assignment for tickets marked as critical safety hazards.',
        ],
      };
    }
    return null;
  }

  /**
   * 3. Analyze high priority work orders
   */
  analyzePriority(workOrders, stats) {
    const highPriorityCount = stats.high_priority || 0;

    if (highPriorityCount > 0) {
      const severity = highPriorityCount > 15 ? 'critical' : highPriorityCount > 5 ? 'warning' : 'info';

      return {
        id: 'local-priority',
        type: 'warning',
        severity,
        icon: 'MdPriorityHigh',
        title: 'Critical Operations Alert',
        message: `${highPriorityCount} critical/high priority work orders require immediate attention to prevent system failure or safety incidents.`,
        action: 'View Priority',
        actionType: 'filter',
        actionPayload: { filter: 'high-priority' },
        metrics: {
          count: highPriorityCount,
          critical: workOrders.filter((wo) => wo.priority === 'critical' && ACTIVE(wo.status)).length,
          high: workOrders.filter((wo) => wo.priority === 'high' && ACTIVE(wo.status)).length,
        },
        recommendations: [
          'Dispatch your most senior/experienced technicians to critical tasks immediately.',
          'Set up SMS/Push notifications for all new critical priority items.',
          'Audit priority classifications to ensure field teams aren\'t over-flagging minor issues.',
        ],
      };
    }
    return null;
  }

  /**
   * 4. Analyze completion rate
   */
  analyzeCompletionRate(workOrders, stats) {
    if (!stats.total || stats.total === 0) return null;

    const rate = (stats.completed / stats.total) * 100;

    if (rate >= 85) {
      return {
        id: 'local-completion-high',
        type: 'success',
        severity: 'success',
        icon: 'MdCheckCircle',
        title: 'Operational Excellence',
        message: `Outstanding ${rate.toFixed(1)}% completion rate this cycle! The team is operating at peak efficiency.`,
        action: null,
        metrics: {
          completionRate: parseFloat(rate.toFixed(1)),
          completed: stats.completed,
          total: stats.total,
        },
        recommendations: [
          'Document current operational workflows as best practices.',
          'Consider establishing a mentorship program utilizing your top performers.',
        ],
      };
    } else if (rate < 45 && stats.total > 20) {
      return {
        id: 'local-completion-low',
        type: 'warning',
        severity: 'warning',
        icon: 'MdTrendingUp',
        title: 'Low Completion Velocity',
        message: `Only ${rate.toFixed(1)}% of tasks are reaching completion. Investigate potential supply chain blockers or staffing shortages.`,
        action: 'Analyze Bottlenecks',
        actionType: 'analyze',
        actionPayload: { metric: 'completion-rate' },
        metrics: {
          completionRate: parseFloat(rate.toFixed(1)),
          completed: stats.completed,
          pending: stats.pending,
          inProgress: stats.in_progress,
        },
        recommendations: [
          'Analyze task complexity and original time estimates versus actual time logged.',
          'Review technician workload and geographic distribution.',
          'Check inventory logs; parts shortages are the #1 cause of stalled completions.',
        ],
      };
    }
    return null;
  }

  /**
   * 5. Analyze workload distribution (Burnout risk detection)
   */
  analyzeWorkload(workOrders, stats) {
    const inProgress = stats.in_progress || 0;
    const pending = stats.pending || 0;

    if (inProgress > (pending * 2.5) && inProgress > 15) {
      return {
        id: 'local-workload',
        type: 'info',
        severity: 'warning',
        icon: 'MdSpeed',
        title: 'High WIP (Work In Progress)',
        message: `${inProgress} tasks are "In Progress" versus ${pending} "Pending". This indicates task switching and potential technician burnout.`,
        action: 'Balance Load',
        actionType: 'balance',
        actionPayload: { redistribute: true },
        metrics: {
          inProgress,
          pending,
          ratio: (inProgress / Math.max(pending, 1)).toFixed(2),
        },
        recommendations: [
          'Enforce strict WIP (Work In Progress) limits per technician (e.g., max 3 active tickets).',
          'Review tickets stuck in progress to identify missing parts or approvals.',
          'Encourage technicians to officially "Pause" tickets they cannot actively work on.',
        ],
      };
    }
    return null;
  }

  /**
   * 6. Analyze trends
   * --- UPDATE: measured from created_at / completed_at in the last 7 vs previous 7 days (no invented %)
   */
  analyzeTrends(workOrders, stats) {
    const now = Date.now();
    const inWindow = (d, from, to) => d && (now - new Date(d).getTime()) / 86400000 >= from && (now - new Date(d).getTime()) / 86400000 < to;
    const created7 = workOrders.filter((w) => inWindow(w.created_at, 0, 7)).length;
    const done7 = workOrders.filter((w) => inWindow(w.completed_at, 0, 7)).length;
    const totalActive = (stats.pending || 0) + (stats.in_progress || 0);

    if (created7 >= 10 && created7 > done7 * 1.2) {
      return {
        id: 'local-trend-backlog',
        type: 'info',
        severity: 'info',
        icon: 'MdTimeline',
        title: 'Negative Trend: Backlog Growth',
        message: `${created7} work orders were created in the last 7 days but only ${done7} were completed, so the backlog is growing.`,
        action: 'View Projections',
        actionType: 'navigate',
        actionPayload: { route: '/admin/reports' },
        metrics: {
          created7d: created7,
          completed7d: done7,
          active: totalActive,
        },
        recommendations: [
          'Review resource allocation and authorize temporary overtime if necessary.',
          'Shift focus purely to high-priority impact tickets to stabilize the system.',
          'Evaluate if recent weather or systemic issues caused a spike in reactive maintenance.',
        ],
      };
    }
    return null;
  }

  /**
   * 7. Analyze resource optimization
   */
  analyzeResources(workOrders, stats) {
    const unassignedCount = stats.unassigned || 0;
    const totalActive = (stats.pending || 0) + (stats.in_progress || 0);

    if (unassignedCount > 5 && totalActive > 20) {
      const utilizationScore = Math.max(0, ((totalActive - unassignedCount) / totalActive) * 100).toFixed(1);

      return {
        id: 'local-resources',
        type: 'info',
        severity: 'info',
        icon: 'MdAutoAwesome',
        title: 'Resource Optimization Available',
        message: `${utilizationScore}% of active work has an owner; ${unassignedCount} items are waiting. The AI dispatcher can assign them by skills, distance and workload.`,
        action: 'Run Optimizer',
        actionType: 'optimize',
        actionPayload: { autoAssign: true },
        metrics: {
          utilization: parseFloat(utilizationScore),
          unassigned: unassignedCount,
          totalActive,
        },
        recommendations: [
          'Enable continuous skill-based and proximity-based auto-assignment.',
          'Track individual technician velocity to improve baseline time estimates.',
        ],
      };
    }
    return null;
  }

  /**
   * 8. Detect Anomalous Durations (Time-based outliers)
   * --- UPDATE: uses actual_duration when present, otherwise started_at/created_at -> completed_at (hours);
   *            robust median/MAD so one huge job does not hide the others
   */
  detectAnomalousDurations(workOrders) {
    if (!workOrders || workOrders.length < 10) return null;

    const completed = workOrders
      .filter((w) => w.status === 'completed')
      .map((w) => ({ w, h: Number(w.actual_duration) || (w.completed_at ? hoursBetween(w.started_at || w.created_at, w.completed_at) : NaN) }))
      .filter((x) => Number.isFinite(x.h) && x.h > 0);
    if (completed.length < 8) return null;

    const sorted = completed.map((x) => x.h).sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const dev = sorted.map((v) => Math.abs(v - median)).sort((a, b) => a - b);
    const mad = dev[Math.floor(dev.length / 2)] || 1e-9;
    const anomalies = completed.filter((x) => (0.6745 * (x.h - median)) / mad > 3);

    if (anomalies.length > 0) {
      return {
        id: 'local-anomaly-duration',
        type: 'warning',
        severity: 'warning',
        icon: 'MdTimerOff',
        title: 'Time Estimation Anomalies',
        message: `Detected ${anomalies.length} recently completed work orders that took significantly longer than usual (median ${median.toFixed(1)} h).`,
        action: 'Review Anomalies',
        actionType: 'filter',
        actionPayload: { filter: 'duration_anomalies', ids: anomalies.map((a) => a.w.id) },
        metrics: {
          anomaliesFound: anomalies.length,
          medianHours: Number(median.toFixed(1)),
        },
        evidence: anomalies.slice(0, 5).map((a) => ({ id: a.w.id, label: a.w.title, hours: Number(a.h.toFixed(1)) })),
        recommendations: [
          'Review notes on these specific tickets to identify unforeseen complications.',
          'Adjust future baseline time estimates for this specific asset class.',
        ],
      };
    }
    return null;
  }

  /**
   * 9. Analyze SLA Adherence Risk
   */
  analyzeSLAAdherence(workOrders) {
    if (!workOrders) return null;

    const now = new Date().getTime();
    // Find tickets due within the next 24 hours that haven't been started
    const atRisk = workOrders.filter((wo) => {
      const dl = deadlineOf(wo);
      if (!ACTIVE(wo.status) || !dl) return false;
      const timeRemaining = new Date(dl).getTime() - now;
      return timeRemaining > 0 && timeRemaining < (24 * 60 * 60 * 1000) && NOT_STARTED.includes(wo.status);
    });

    if (atRisk.length > 3) {
      return {
        id: 'local-sla-risk',
        type: 'warning',
        severity: 'critical',
        icon: 'MdWarning',
        title: 'SLA Violation Risk',
        message: `${atRisk.length} pending tickets are due within 24 hours. Immediate dispatch required to avoid contractual SLA breaches.`,
        action: 'View At-Risk Tickets',
        actionType: 'filter',
        actionPayload: { filter: 'sla_risk', ids: atRisk.map((w) => w.id) },
        metrics: {
          atRiskCount: atRisk.length,
        },
        recommendations: [
          'Force-assign these tickets to the closest active technician.',
          'Consider pausing preventative maintenance to clear reactive SLA-bound tickets.',
        ],
      };
    }
    return null;
  }

  /**
   * 10. Analyze Asset Failure Patterns
   */
  analyzeAssetFailurePatterns(workOrders) {
    if (!workOrders) return null;

    // Group active tickets by asset_id
    const assetFailures = {};
    workOrders.forEach((wo) => {
      if (wo.asset_id && ACTIVE(wo.status)) {
        assetFailures[wo.asset_id] = (assetFailures[wo.asset_id] || 0) + 1;
      }
    });

    // Find assets with >= 3 concurrent active tickets
    const problematicAssets = Object.entries(assetFailures)
      .filter(([, count]) => count >= 3)
      .map(([id, count]) => ({ assetId: id, count }));

    if (problematicAssets.length > 0) {
      return {
        id: 'local-asset-clusters',
        type: 'warning',
        severity: 'critical',
        icon: 'MdPrecisionManufacturing',
        title: 'Cascading Asset Failures Detected',
        message: `${problematicAssets.length} physical assets currently have 3 or more concurrent active work orders. This indicates fundamental system degradation.`,
        action: 'Launch Digital Twin',
        actionType: 'navigate',
        actionPayload: { route: '/admin/digital-twin' },
        metrics: {
          assetsAffected: problematicAssets.length,
          maxTicketsOnSingleAsset: Math.max(...problematicAssets.map((a) => a.count)),
        },
        evidence: problematicAssets.slice(0, 5).map((a) => ({ id: a.assetId, open: a.count })),
        recommendations: [
          'Launch the Digital Twin to run a root-cause simulation on the affected asset cluster.',
          'Halt patch-repairs and evaluate the asset for full replacement/overhaul.',
        ],
      };
    }
    return null;
  }

  /**
   * 11. --- UPDATE: work stuck on hold for more than 3 days
   */
  analyzeOnHold(workOrders) {
    const now = Date.now();
    const stuck = workOrders.filter((w) => w.status === 'on_hold' && (now - new Date(w.on_hold_at || w.updated_at || now).getTime()) > 3 * 86400000);
    if (!stuck.length) return null;
    return {
      id: 'local-on-hold',
      type: 'warning',
      severity: stuck.length > 10 ? 'warning' : 'info',
      icon: 'MdPauseCircle',
      title: 'Work stalled on hold',
      message: `${stuck.length} work orders have been on hold for more than 3 days.`,
      action: 'View On Hold',
      actionType: 'filter',
      actionPayload: { filter: 'status', value: 'on_hold' },
      metrics: { count: stuck.length },
      recommendations: ['Check Inventory and purchase orders for the blocking parts.', 'Close or re-plan items that cannot proceed.'],
    };
  }

  /**
   * 12. --- UPDATE: field work raised from public (phone/helpdesk) reports
   */
  analyzePublicReports(workOrders) {
    const pub = workOrders.filter((w) => w.source === 'helpdesk' && ACTIVE(w.status));
    if (pub.length < 3) return null;
    const avgDays = pub.reduce((a, w) => a + hoursBetween(w.created_at, new Date()), 0) / pub.length / 24;
    if (avgDays < 3) return null;
    return {
      id: 'local-public-backlog',
      type: 'info',
      severity: 'warning',
      icon: 'MdPhoneInTalk',
      title: 'Public reports waiting on field work',
      message: `${pub.length} work orders from public reports are open, on average for ${avgDays.toFixed(1)} days. Callers are texted when work starts and when it is fixed.`,
      action: 'Open Public Reports',
      actionType: 'navigate',
      actionPayload: { route: '/admin/road-works?tab=public' },
      metrics: { open: pub.length, avgAgeDays: Number(avgDays.toFixed(1)) },
      recommendations: ['Prioritise hot spots reported by several callers.'],
    };
  }


  // ────────────────────────────────────────────────────────────────────────────
  // EXPORT UTILITIES
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Export insights to a formatted JSON string
   * @param {Array} insights
   * @returns {string}
   */
  exportInsights(insights) {
    this.log('info', 'Exporting insights to JSON', { count: insights?.length });

    if (!insights || insights.length === 0) return JSON.stringify({});

    const exportData = {
      generatedAt: new Date().toISOString(),
      system: 'FlowSense AI Insights Module',
      version: this.version,
      insightCount: insights.length,
      context: this.lastMeta ? { engineMs: this.lastMeta.durationMs, source: this.lastMeta.source, briefing: this.lastMeta.narrative } : null,
      insights: insights.map((insight) => ({
        ...insight,
        exportedAt: new Date().toISOString(),
      })),
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Utility to map JSON insights directly to a CSV format string
   * @param {Array} insights
   * @returns {string} CSV formatted text
   */
  exportToCSV(insights) {
    this.log('info', 'Exporting insights to CSV', { count: insights?.length });
    if (!insights || insights.length === 0) return '';

    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['ID', 'Severity', 'Title', 'Message', 'Action', 'Source', 'Score', 'Created At'];
    const created = new Date().toISOString();

    const rows = insights.map((i) => [
      esc(i.id),
      esc(i.severity || i.type),
      esc(i.title),
      esc(i.message),
      esc(i.action || 'None'),
      esc(i.source || ''),
      esc(i.score ?? ''),
      esc(i.generatedAt || created),
    ].join(','));

    return [headers.join(','), ...rows].join('\n');
  }
}

// Create and export singleton instance
const aiInsightsService = new AIInsightsService();

export default aiInsightsService;
